
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next'; // Import translation hook
import { User, Document, DocumentType } from '../types';
import { api } from '../services/api';
import { isFinancialDocument } from '../utils/financialUtils';
import { FileText, Loader2, Wallet, CheckCircle } from 'lucide-react';
import { ClientFinancialsTable } from './ClientFinancialsTable';

interface ClientDashboardProps {
    user: User;
    onViewDocuments: () => void;
    onRefreshDocuments?: () => void;
}

const ClientDashboard: React.FC<ClientDashboardProps> = ({ user, onViewDocuments, onRefreshDocuments }) => {
    const { t } = useTranslation(['dashboard']); // Init hook
    const [agencyName, setAgencyName] = useState<string>('Agency');
    const [docCount, setDocCount] = useState<number>(0);
    const [newDocCount, setNewDocCount] = useState<number>(0);
    const [documents, setDocuments] = useState<Document[]>([]);
    const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const refreshDashboard = React.useCallback(async () => {
        try {
            setLoading(true);
            // 1. Fetch Agency Name
            if (user.agencyId) {
                try {
                    const profile = await api.getPublicProfile(user.agencyId);
                    setAgencyName(profile.companyName || 'Agency');
                } catch (e) {
                    console.error("Failed to fetch agency name", e);
                }
            }

            // 2. Fetch Document Count & Types
            if (user.agencyId) {
                const [docs, types] = await Promise.all([
                    api.getDocuments(user.agencyId, user.id),
                    api.getDocumentTypes(user.agencyId, true) // Pass true for Client View (filters visible types)
                ]);

                setDocCount(docs.length);
                setDocuments(docs);
                setDocumentTypes(types);

                // Count docs that haven't been viewed by the client AND were not uploaded by the client
                const newDocs = docs.filter(doc => !doc.clientViewed && doc.uploadedByRole !== 'client');
                setNewDocCount(newDocs.length);
            }
        } catch (error) {
            console.error("Dashboard Load Error:", error);
        } finally {
            setLoading(false);
        }
    }, [user.id, user.agencyId]);

    useEffect(() => {
        refreshDashboard();
    }, [refreshDashboard]);

    // Determine Client Name for Welcome Message
    const clientName = user.profile?.companyName || user.config?.companyName || user.email || 'Client';

    return (
        <div className="animate-fadeIn">
            {/* Header Section */}
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-slate-900">
                    {t('client.welcome', { name: clientName })}
                </h1>
                <p className="text-slate-500 mt-1">
                    {t('client.subtitle')}
                </p>
                <div className="mt-2 text-sm font-medium text-slate-400">
                    {t('client.partnered_with')} <span className="text-slate-600 font-bold">{agencyName}</span>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* Total Documents Banner */}
                <div
                    onClick={onViewDocuments}
                    className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-blue-300 transition-all cursor-pointer hover:shadow-md active:scale-95"
                >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                    <div className="relative z-10">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-4">
                            <FileText size={20} />
                        </div>
                        <p className="text-slate-500 text-sm font-medium uppercase tracking-wide mb-1">{t('client.total_documents')}</p>
                        <h3 className="text-3xl font-bold text-slate-900">{docCount}</h3>
                    </div>
                </div>

                {/* New Documents Banner */}
                <div
                    onClick={onViewDocuments}
                    className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-green-300 transition-all cursor-pointer hover:shadow-md active:scale-95"
                >
                    <div className="absolute top-0 right-0 w-24 h-24 bg-green-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                    <div className="relative z-10">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center text-green-600 mb-4">
                            <FileText size={20} />
                            {newDocCount > 0 && <span className="absolute top-2 right-2 w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>}
                        </div>
                        <p className="text-slate-500 text-sm font-medium uppercase tracking-wide mb-1">{t('client.new_documents')}</p>
                        <h3 className="text-3xl font-bold text-slate-900">{newDocCount}</h3>
                    </div>
                </div>

                {/* Monthly Obligations Banner */}
                {(() => {
                    // Calculate Monthly Obligations Stats
                    const currentYear = new Date().getFullYear();
                    const currentMonth = new Date().getMonth();

                    const monthlyDocs = documents.filter(doc => {
                        // 1. Must be Financial Document
                        if (!isFinancialDocument(doc, documentTypes)) return false;

                        // 2. Must be in Current Month
                        // Priority: Installment Date > Doc Date > Created Date
                        const dateStr = doc.ai_analysis?.installment_info?.installment_date || doc.ai_analysis?.doc_date || doc.createdAt;
                        if (!dateStr) return false;

                        const date = new Date(dateStr);
                        return date.getFullYear() === currentYear && date.getMonth() === currentMonth;
                    });

                    const total = monthlyDocs.length;
                    if (total === 0) return null; // Hide if no obligations this month

                    const paid = monthlyDocs.filter(doc => doc.paymentStatus === 'paid').length;
                    const pending = total - paid;
                    const percentage = Math.round((paid / total) * 100);

                    // Circular Progress Calculation
                    const radius = 18;
                    const circumference = 2 * Math.PI * radius;
                    const strokeDashoffset = circumference - (percentage / 100) * circumference;

                    return (
                        <div
                            onClick={onViewDocuments}
                            className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden group hover:border-purple-300 transition-all cursor-pointer hover:shadow-md active:scale-95"
                        >
                            <div className="absolute top-0 right-0 w-24 h-24 bg-purple-50 rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110"></div>
                            <div className="relative z-10">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center text-purple-600">
                                        <Wallet size={20} />
                                    </div>
                                    <span className="text-xs font-bold text-purple-700 bg-purple-50 px-2 py-1 rounded-lg">
                                        {paid}/{total}
                                    </span>
                                </div>
                                <p className="text-slate-500 text-sm font-medium uppercase tracking-wide mb-1">{t('client.monthly_obligations')}</p>
                                <div className="flex items-baseline gap-2 mb-3">
                                    <h3 className="text-3xl font-bold text-slate-900">{percentage}%</h3>
                                    <span className="text-sm font-medium text-slate-500">{t('client.completed')}</span>
                                </div>

                                {/* Animated Progress Bar */}
                                <div className="w-full bg-slate-100 rounded-full h-2 mb-2 overflow-hidden">
                                    <div
                                        className="bg-purple-600 h-2 rounded-full transition-all duration-1000 ease-out"
                                        style={{ width: `${percentage}%` }}
                                    ></div>
                                </div>

                                <p className="text-xs text-slate-400 font-medium">
                                    {t('client.pending_docs', { count: pending })}
                                </p>
                            </div>
                        </div>
                    );
                })()}
            </div>

            {/* Financial Overview Table */}
            {user.config?.ai_features?.document_ai_enabled && documents.length > 0 && (
                <ClientFinancialsTable
                    documents={documents}
                    documentTypes={documentTypes}
                    dateFormat={user.dateFormat}
                    onRefresh={() => {
                        refreshDashboard();
                        if (onRefreshDocuments) onRefreshDocuments();
                    }}
                />
            )}
        </div>
    );
};

export default ClientDashboard;
